import { ApiError, api, getCsrfToken } from "./client";
import type { HomeBlock, HomeBlockInput, HomeBlockReorderItem } from "./types";

/** docs/API_CONTRACT.md - GET /api/admin/home-blocks (draft and published) */
export function listHomeBlocks(): Promise<HomeBlock[]> {
  return api.get<HomeBlock[]>("/admin/home-blocks");
}

/** docs/API_CONTRACT.md - POST /api/admin/home-blocks */
export function createHomeBlock(payload: HomeBlockInput): Promise<HomeBlock> {
  return api.post<HomeBlock>("/admin/home-blocks", payload);
}

/** docs/API_CONTRACT.md - PUT /api/admin/home-blocks/{blockId} */
export function updateHomeBlock(
  blockId: string,
  payload: HomeBlockInput,
): Promise<HomeBlock> {
  return api.put<HomeBlock>(`/admin/home-blocks/${encodeURIComponent(blockId)}`, payload);
}

/** docs/API_CONTRACT.md - DELETE /api/admin/home-blocks/{blockId} */
export function deleteHomeBlock(blockId: string): Promise<{ id: string; deleted: boolean }> {
  return api.delete<{ id: string; deleted: boolean }>(`/admin/home-blocks/${encodeURIComponent(blockId)}`);
}

/** docs/API_CONTRACT.md - POST /api/admin/home-blocks/bulk-delete */
export function bulkDeleteHomeBlocks(ids: string[]): Promise<{ deleted: Array<{ id: string; deleted: boolean }> }> {
  return api.post<{ deleted: Array<{ id: string; deleted: boolean }> }>("/admin/home-blocks/bulk-delete", { ids });
}

/** docs/API_CONTRACT.md - POST /api/admin/home-blocks/reorder */
export function reorderHomeBlocks(items: HomeBlockReorderItem[]): Promise<HomeBlock[]> {
  return api.post<HomeBlock[]>("/admin/home-blocks/reorder", { items });
}

/** docs/API_CONTRACT.md - POST /api/admin/home-blocks/{blockId}/publish */
export function publishHomeBlock(blockId: string): Promise<HomeBlock> {
  return api.post<HomeBlock>(
    `/admin/home-blocks/${encodeURIComponent(blockId)}/publish`,
  );
}

/** docs/API_CONTRACT.md - POST /api/admin/home-blocks/{blockId}/unpublish */
export function unpublishHomeBlock(blockId: string): Promise<HomeBlock> {
  return api.post<HomeBlock>(
    `/admin/home-blocks/${encodeURIComponent(blockId)}/unpublish`,
  );
}

export interface HomeBlockMediaUpload {
  id: string;
  url: string;
  contentType: string;
  size: number;
  width: number;
  height: number;
}

export async function uploadHomeBlockMedia(file: File): Promise<HomeBlockMediaUpload> {
  const formData = new FormData();
  formData.append("file", file);
  const csrfToken = getCsrfToken();
  const response = await fetch("/api/admin/media/home-blocks", {
    method: "POST",
    credentials: "include",
    headers: csrfToken ? { "X-CSRF-Token": csrfToken, Accept: "application/json" } : { Accept: "application/json" },
    body: formData,
  });
  if (!response.ok) {
    throw new ApiError(
      response.status,
      "media_upload_failed",
      "No se pudo subir la imagen.",
      undefined,
      response.headers.get("X-Request-Id"),
    );
  }
  return (await response.json()) as HomeBlockMediaUpload;
}
